import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred'
import WarningIcon from '@mui/icons-material/Warning'
import { Box, Button, Popover, Typography, useTheme } from '@mui/material'
import { useEntitiesFromSearch } from 'api/search'
import { useMemo } from 'react'
import RenderCompanyChip from '../RenderChipComponents/RenderCompanyChip'
import {
  boxContainerStyles,
  boxStyles,
  buttonContainerStyles,
  deleteButtonStyles,
  popoverStyles,
  textStyles,
} from './style'
import './style.css'

const OrganizationsPopover = ({ id, open, anchorEl, onClose, handleConfirmDelete, selectedData }) => {
  const theme = useTheme()
  const PK = selectedData?.PK ?? null

  // Use already-loaded data from bootstrap instead of additional queries
  const { deals: allDeals, organizations: allOrganizations } = useEntitiesFromSearch()

  // Filter deals that belong to this organization (deals have PK matching org PK)
  const orgDeals = useMemo(() => {
    if (!PK || !allDeals?.length) return []
    return allDeals.filter(deal => deal.PK === PK)
  }, [PK, allDeals])

  // Get contracted by organizations
  const contractedBy = useMemo(() => {
    const contractedByIds = selectedData?.contractedBy ?? []
    if (!contractedByIds.length || !allOrganizations?.length) return []
    return allOrganizations.filter(org => contractedByIds.includes(org.org_uuid))
  }, [selectedData?.contractedBy, allOrganizations])

  // Get affiliated organizations
  const affiliated = useMemo(() => {
    const affiliatedIds = selectedData?.affiliated ?? []
    if (!affiliatedIds.length || !allOrganizations?.length) return []
    return allOrganizations.filter(org => affiliatedIds.includes(org.org_uuid))
  }, [selectedData?.affiliated, allOrganizations])

  const hasDeals = orgDeals.length > 0

  // Don't render popover if not open or no valid anchorEl
  if (!open || !anchorEl) {
    return null
  }

  return (
    <>
      <Box>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          sx={popoverStyles(theme)}
        >
          <Box sx={boxContainerStyles(theme)}>
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningIcon sx={{ color: 'crimson' }} />
                <Typography sx={{ fontWeight: 'bold', color: 'crimson' }}>Confirm Deletion</Typography>
              </Box>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to permanently delete the following item(s) from Grobotic?
              </Typography>
              {selectedData && selectedData.name && (
                <>
                  <Box sx={boxStyles(theme)}>
                    <CorporateFareIcon
                      sx={{
                        color: theme.palette.icon.organizations,
                        fontSize: 35,
                      }}
                    />
                    <Typography style={textStyles(theme)}>{selectedData.name}</Typography>
                  </Box>
                  <Box sx={boxStyles(theme)}>
                    <Typography>Linked Data:</Typography>
                  </Box>
                  <Box sx={{ ...boxStyles(theme), flexDirection: 'column' }}>
                    {contractedBy.length > 0 &&
                      contractedBy.map(org => (
                        <Box key={org?.org_uuid} className="parent-orgs-container">
                          <Typography
                            className="parent-orgs-label"
                            sx={{ '--text-color': theme.palette.text.highlight }}
                          >
                            Contracted By
                          </Typography>
                          <RenderCompanyChip option={org} />
                        </Box>
                      ))}
                    {affiliated.length > 0 &&
                      affiliated.map(org => (
                        <Box key={org?.org_uuid} className="parent-orgs-container">
                          <Typography
                            className="parent-orgs-label"
                            sx={{ '--text-color': theme.palette.text.highlight }}
                          >
                            Affiliated
                          </Typography>
                          <RenderCompanyChip option={org} />
                        </Box>
                      ))}
                  </Box>
                  {hasDeals && (
                    <>
                      <Box sx={{ ...boxStyles(theme), flexDirection: 'column', justifyContent: 'space-between' }}>
                        {orgDeals.map(deal => (
                          <Box
                            key={deal?.deal_uuid}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 5,
                              minWidth: '100%',
                            }}
                          >
                            <Box>
                              <AttachMoneyIcon
                                sx={{
                                  color: theme.palette.icon.deals,
                                }}
                              />
                            </Box>
                            <Box>
                              <Typography>{deal.name}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ ...boxStyles(theme), gap: 1 }}>
                        <ReportGmailerrorredIcon sx={{ color: 'rgba(255, 193, 7)' }} />
                        <Typography sx={{ color: 'rgba(255, 193, 7)' }}>
                          {`Organization has ${orgDeals.length} connected deal(s) - remove before deleting organization`}
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              )}
            </>

            <Box sx={buttonContainerStyles(theme)}>
              <Button
                sx={deleteButtonStyles(theme)}
                disabled={hasDeals}
                variant="outlined"
                onClick={handleConfirmDelete}
                color="secondary"
              >
                Confirm
              </Button>
              <Button variant="contained" onClick={onClose}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </>
  )
}
export default OrganizationsPopover
